import React, { Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import Spinner from '~/components/shared/Spinner/SpinnerCircular';
import constVars from '~/constants/constVars';
import { Redirect } from 'react-router';
import { Loader } from '@qred/components-library';
import Eskat from './pages/Eskat/Eskat';
import ProtectedRoute from './ProtectedRoute';
import LoanApplication from './pages/LoanApplication/LoanApplication';
import CardOffer from './pages/CardOffer/CardOffer';
import ThankYouCardOffer from './pages/ThankyouCardOffer/ThankyouCardOffer';
import ThankYouCardWaitingAdditionalSigner from './pages/ThankyouCardWaitingAdditionalSigner/ThankyouCardWaitingAdditionalSigner';
import LoanAdditionalSigner from './pages/AdditionalSigner/LoanAdditionalSigner/LoanAdditionalSigner';
import AdditionalSignerOTP from './pages/AdditionalSigner/AdditionalSignerOTP/AdditionalSignerOTP';
import SecondaryLayout from './components/shared/Layout/SecondaryLayout';
import Maintenance from './pages/Maintenance/Maintenance';
import { CardAdditionalSigner } from './pages/AdditionalSigner/CardAdditionalSigner/CardAdditionalSigner';
import { useMaintenanceFeatureFlagEnabled } from './hooks/useMaintenanceFeatureFlagEnabled';
import AdditionalSignerError from './pages/AdditionalSigner/AdditionalSignerError';
import LoanAdditionalSignerSuccess from './pages/AdditionalSigner/LoanAdditionalSigner/LoanAdditionalSignerSuccess';
import BankConnectionStandalone from './pages/BankConnectionStandalone/BankConnectionStandalone';
import BankConnectionResult from './pages/BankConnectionResult/BankConnectionResult';
import { CardApplication } from './pages/CardApplication/CardApplication';
import UpdateApplicationStandalone from './pages/UpdateApplicationStandalone/UpdateApplicationStandalone';
import { SvcOnboardingAuthErrorScreen } from './SvcOnboardingAuthErrorScreen';

const LoanOffer = React.lazy(() => import('./pages/LoanOffer/LoanOffer'));
const PageNotFound = React.lazy(
  () => import('./pages/PageNotFound/PageNotFound')
);
const Login = React.lazy(() => import('./pages/Login/Login'));

const Routes = () => {
  const isFlagEnabled = useMaintenanceFeatureFlagEnabled();
  if (
    isFlagEnabled &&
    window.location.pathname !== `/${constVars.ROUTE_MAINTENANCE}`
  ) {
    return <Redirect to={`/${constVars.ROUTE_MAINTENANCE}`} />;
  }

  return (
    <Switch>
      <Route exact path={`/${constVars.ROUTE_MAINTENANCE}`}>
        <SecondaryLayout>
          <Maintenance />
        </SecondaryLayout>
      </Route>

      <Redirect
        exact
        from={`/${constVars.ROUTE_LOAN_OFFER_OLD}/:applicationUuid`}
        to={{
          pathname: `/${constVars.ROUTE_LOAN_OFFER}/:applicationUuid`,
          search: window.location.search,
        }}
      />
      <ProtectedRoute
        exact
        path={`/${constVars.ROUTE_LOAN_OFFER}/:applicationUuid`}
      >
        <Suspense fallback={<Spinner />}>
          <LoanOffer />
        </Suspense>
      </ProtectedRoute>

      <Redirect
        exact
        from={`/${constVars.ROUTE_CARD_OFFER_OLD}/:clientId`}
        to={{
          pathname: `/${constVars.ROUTE_CARD_OFFER}/:clientId`,
          search: window.location.search,
        }}
      />
      <ProtectedRoute exact path={`/${constVars.ROUTE_CARD_OFFER}/:clientId`}>
        <Suspense fallback={<Spinner />}>
          <CardOffer />
        </Suspense>
      </ProtectedRoute>

      <Route path={`/${constVars.ROUTE_THANK_YOU_CONTINUE_IN_APP}`}>
        <ThankYouCardOffer />
      </Route>

      <Route path={`/${constVars.ROUTE_THANK_YOU_WAITING_ADDITIONAL_SIGNER}`}>
        <ThankYouCardWaitingAdditionalSigner />
      </Route>

      {/* ADDITIONAL SIGNER PAGES */}
      <Redirect
        exact
        from={`/${constVars.ROUTE_ADDITIONAL_SIGNER_AUTH_OLD}`}
        to={{
          pathname: `/${constVars.ROUTE_ADDITIONAL_SIGNER_AUTH}`,
          search: window.location.search,
        }}
      />
      <Route exact path={`/${constVars.ROUTE_ADDITIONAL_SIGNER_AUTH}`}>
        <SecondaryLayout>
          <AdditionalSignerOTP />
        </SecondaryLayout>
      </Route>

      <Redirect
        exact
        from={`/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER_OLD}`}
        to={{
          pathname: `/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER}`,
          search: window.location.search,
        }}
      />
      <Route exact path={`/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER}`}>
        <SecondaryLayout>
          <LoanAdditionalSigner />
        </SecondaryLayout>
      </Route>

      <Redirect
        exact
        from={`/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER_OLD}/:type(${constVars.PARAMS_SIGNER_SIGNED_SUCCESS})`}
        to={`/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER}/:type(${constVars.PARAMS_SIGNER_SIGNED_SUCCESS})`}
      />
      <Route
        exact
        path={`/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER}/:type(${constVars.PARAMS_SIGNER_SIGNED_SUCCESS})`}
      >
        <SecondaryLayout>
          <LoanAdditionalSignerSuccess />
        </SecondaryLayout>
      </Route>

      <Redirect
        exact
        from={`/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER_OLD}/:type(${constVars.PARAMS_SIGNER_ALREADY_SIGNED})`}
        to={`/${constVars.ROUTE_SIGNER_ERROR}`}
      />
      <Route exact path={`/${constVars.ROUTE_SIGNER_ERROR}`}>
        <SecondaryLayout>
          <AdditionalSignerError />
        </SecondaryLayout>
      </Route>

      <Redirect
        exact
        from={`/${constVars.ROUTE_CARD_ADDITIONAL_SIGNER_OLD}`}
        to={{
          pathname: `/${constVars.ROUTE_CARD_ADDITIONAL_SIGNER}`,
          search: window.location.search,
        }}
      />
      <Route exact path={`/${constVars.ROUTE_CARD_ADDITIONAL_SIGNER}`}>
        <SecondaryLayout>
          <CardAdditionalSigner />
        </SecondaryLayout>
      </Route>

      {/* ADDITIONAL SIGNER PAGES END */}

      <Route
        exact
        path={[
          `/${constVars.ROUTE_ESKAT}`,
          `/${constVars.ROUTE_ESKAT}/redirected`,
        ]}
      >
        <Eskat />
      </Route>

      <Redirect
        exact
        from={`/${constVars.ROUTE_LOAN_APPLICATION_OLD}`}
        to={{
          pathname: `/${constVars.ROUTE_LOAN_APPLICATION}`,
          search: window.location.search,
        }}
      />
      <Route exact path={`/${constVars.ROUTE_LOAN_APPLICATION}`}>
        <LoanApplication />
      </Route>

      <Route exact path={`/${constVars.ROUTE_CARD_APPLICATION}`}>
        <CardApplication />
      </Route>

      <Route exact path={`/${constVars.ROUTE_BANK_CONNECTION_RESULT}`}>
        <BankConnectionResult />
      </Route>

      <Route exact path={`/${constVars.ROUTE_BANK_CONNECTION}`}>
        <BankConnectionStandalone />
      </Route>

      <Route exact path={`/${constVars.ROUTE_UPDATE_APPLICATION}`}>
        <UpdateApplicationStandalone />
      </Route>

      <Route exact path={`/${constVars.ROUTE_SVC_ONBOARDING_AUTH_ERROR}`}>
        <SvcOnboardingAuthErrorScreen />
      </Route>

      <Route exact path={`/${constVars.ROUTE_LOGIN}`}>
        <Suspense fallback={<Loader />}>
          <Login />
        </Suspense>
      </Route>

      <Redirect
        exact
        from="/"
        to={{
          pathname: `/${constVars.ROUTE_LOAN_APPLICATION}`,
        }}
      />

      <Route
        exact
        path="/404"
        render={() => (
          <Suspense fallback={<Spinner />}>
            <PageNotFound />
          </Suspense>
        )}
      />

      <Route
        exact
        path="*"
        render={() => (
          <Suspense fallback={<Spinner />}>
            <PageNotFound />
          </Suspense>
        )}
      />
    </Switch>
  );
};

export default Routes;
